import {
  AIStarsIcon,
  Chevron,
  Disclaimer,
  Div,
  H3,
  H5,
  Icon,
  ImagePlaceholderCircle,
  Img,
  P,
  UndoIcon,
  centsToDollars,
  displayCentsInDollars,
  formatter,
  lightTheme,
  noItems,
} from '@vgrubs/components';
import {
  CategoryV2Response,
  MenuItemV2Response,
  MenuV2Response,
} from 'components/src/types/menu.types';
import { useEffect, useRef, useState } from 'react';
import {
  MenuAiItemChange,
  MenuAiModifierChange,
  PriceChangeRow,
} from '../../../types/api/menuAi.types';
import { Collapsible, ItemListImage } from '../MenyStyled';
import { hasItemChange } from './menuAiChanges.helpers';
import { ItemGlare, RefineIconButton } from './menuAiGlow.styled';
import { deltaColor, signedCents, signedPct } from './priceChangeFormat';

/** DOM id of a preview item — used to scroll it into view from the Changes list. */
export const aiItemDomId = (itemId: number) => `ai-item-${itemId}`;

type OnRefine = (
  item: MenuItemV2Response,
  category: CategoryV2Response
) => void;

// itemId → its price-change row, so a changed item can show old → new → delta.
type PriceByItem = Map<number, PriceChangeRow>;
// itemId → its changed modifier options, shown under the item like its price.
type ModifiersByItem = Map<number, MenuAiModifierChange[]>;
// itemId → its item change, so the preview can show the struck-through old
// name / description (the draft menu already carries the new values).
type ChangeByItem = Map<number, MenuAiItemChange>;

/**
 * Read-only preview of an (AI draft) menu — the item list grouped by category,
 * with a per-item "refine with AI" affordance (shown only when `onRefine` is
 * provided). No tab bar: the AI flow only previews items.
 *
 * Category expand/collapse is CONTROLLED by the parent (via `collapsedIds` +
 * `onToggleCategory`) so a single "Collapse all categories" button can drive
 * every category at once.
 */
export const MenuAIPreviewSections = ({
  menu,
  onRefine,
  onRevert,
  onItemClick,
  refineDisabled,
  priceChanges,
  changes,
  collapsedIds,
  onToggleCategory,
  highlightItemId,
}: {
  menu: MenuV2Response | null;
  onRefine?: OnRefine;
  // Revert a whole item back to the original menu (shown only on changed items).
  onRevert?: (itemId: number) => void;
  // Focus the item's entry in the Changes panel (scroll + glare) on click —
  // only wired for changed items (the reverse of the Changes → preview focus).
  onItemClick?: (itemId: number) => void;
  // Keep the refine affordance mounted but inert (e.g. during a busy re-run).
  refineDisabled?: boolean;
  priceChanges?: PriceChangeRow[];
  // Item changes — read for their nested modifier price changes + revertability.
  changes?: MenuAiItemChange[];
  collapsedIds: Set<number>;
  onToggleCategory: (categoryId: number) => void;
  // Item to play a one-shot glare on (set when focused from the Changes list).
  highlightItemId?: number | null;
}) => {
  const priceByItem: PriceByItem = new Map(
    (priceChanges ?? [])
      .filter((r) => r.deltaCents !== 0)
      .map((r) => [r.itemId, r])
  );
  const modifiersByItem: ModifiersByItem = new Map(
    (changes ?? [])
      .filter((c) => !!c.modifiers && c.modifiers.length > 0)
      .map((c) => [c.itemId, c.modifiers as MenuAiModifierChange[]])
  );
  const changeByItem: ChangeByItem = new Map(
    (changes ?? []).map((c) => [c.itemId, c])
  );
  // Only items the AI actually touched get a Revert affordance.
  const revertableIds = new Set(
    (changes ?? []).filter(hasItemChange).map((c) => c.itemId)
  );
  return (
    <ItemsReadOnly
      menu={menu}
      onRefine={onRefine}
      onRevert={onRevert}
      onItemClick={onItemClick}
      refineDisabled={refineDisabled}
      priceByItem={priceByItem}
      modifiersByItem={modifiersByItem}
      changeByItem={changeByItem}
      revertableIds={revertableIds}
      collapsedIds={collapsedIds}
      onToggleCategory={onToggleCategory}
      highlightItemId={highlightItemId}
    />
  );
};

const ItemsReadOnly = ({
  menu,
  onRefine,
  onRevert,
  onItemClick,
  refineDisabled,
  priceByItem,
  modifiersByItem,
  changeByItem,
  revertableIds,
  collapsedIds,
  onToggleCategory,
  highlightItemId,
}: {
  menu: MenuV2Response | null;
  onRefine?: OnRefine;
  onRevert?: (itemId: number) => void;
  onItemClick?: (itemId: number) => void;
  refineDisabled?: boolean;
  priceByItem: PriceByItem;
  modifiersByItem: ModifiersByItem;
  changeByItem: ChangeByItem;
  revertableIds: Set<number>;
  collapsedIds: Set<number>;
  onToggleCategory: (categoryId: number) => void;
  highlightItemId?: number | null;
}) => {
  if (!menu || menu.categories.length === 0) {
    return (
      <Div $flex $column $alignCenter>
        <Img $width="180px" src={noItems} $mb="24" />
        <H3 $mb="12" $center>
          No Items
        </H3>
      </Div>
    );
  }

  return (
    <>
      {menu.categories.map((category) => (
        <ReadOnlyCategoryItems
          key={category.id}
          category={category}
          onRefine={onRefine}
          onRevert={onRevert}
          onItemClick={onItemClick}
          refineDisabled={refineDisabled}
          priceByItem={priceByItem}
          modifiersByItem={modifiersByItem}
          changeByItem={changeByItem}
          revertableIds={revertableIds}
          expanded={!collapsedIds.has(category.id)}
          onToggle={() => onToggleCategory(category.id)}
          highlightItemId={highlightItemId}
        />
      ))}
    </>
  );
};

const ReadOnlyCategoryItems = ({
  category,
  onRefine,
  onRevert,
  onItemClick,
  refineDisabled,
  priceByItem,
  modifiersByItem,
  changeByItem,
  revertableIds,
  expanded,
  onToggle,
  highlightItemId,
}: {
  category: CategoryV2Response;
  onRefine?: OnRefine;
  onRevert?: (itemId: number) => void;
  onItemClick?: (itemId: number) => void;
  refineDisabled?: boolean;
  priceByItem: PriceByItem;
  modifiersByItem: ModifiersByItem;
  changeByItem: ChangeByItem;
  revertableIds: Set<number>;
  expanded: boolean;
  onToggle: () => void;
  highlightItemId?: number | null;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (ref.current) setHeight(ref.current.clientHeight);
  }, [category, expanded]);

  const handleToggle = () => {
    const willExpand = !expanded;
    onToggle();
    // When opening a category, bring its header to the top of the scroll area
    // so the newly revealed items are in view (works for the desktop card
    // scroll and the mobile whole-group scroll — scrollIntoView walks up to the
    // nearest scrollable ancestor either way).
    if (willExpand) {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    // scroll-margin-top leaves a 32px gap above the header when `scrollIntoView`
    // brings this category to the top of the scroll area (on expand).
    <div ref={rootRef} style={{ scrollMarginTop: '32px' }}>
      <Div
        $box
        $p="16px 24px"
        $pSM="16px 16px"
        $flex
        $alignCenter
        $between
        $mb="8"
        $background={expanded && lightTheme.primary}
        $pointer
        onClick={handleToggle}
      >
        <Div $flex $alignCenter $gap="8px">
          <P $bold $m="0" $color={expanded && lightTheme.white}>
            {category.name} ({category.items.length})
          </P>
        </Div>
        <Icon
          $pointer
          $rotate={expanded ? '360deg' : '180deg'}
          color={expanded ? lightTheme.white : lightTheme.darkGray}
        >
          <Chevron />
        </Icon>
      </Div>

      <Collapsible expanded={expanded} elementHeight={height}>
        <div ref={ref}>
          {category.items.map((item) => (
            <ReadOnlyMenuItem
              key={item.id}
              item={item}
              onRefine={onRefine ? () => onRefine(item, category) : undefined}
              onRevert={
                onRevert && revertableIds.has(item.id)
                  ? () => onRevert(item.id)
                  : undefined
              }
              onItemClick={
                onItemClick && revertableIds.has(item.id)
                  ? () => onItemClick(item.id)
                  : undefined
              }
              refineDisabled={refineDisabled}
              priceChange={priceByItem.get(item.id)}
              modifierChanges={modifiersByItem.get(item.id)}
              change={changeByItem.get(item.id)}
              highlighted={highlightItemId === item.id}
            />
          ))}
          {category.items.length === 0 && (
            <P $m="0" $center>
              No items
            </P>
          )}
        </div>
      </Collapsible>
    </div>
  );
};

const ReadOnlyMenuItem = ({
  item,
  onRefine,
  onRevert,
  onItemClick,
  refineDisabled,
  priceChange,
  modifierChanges,
  change,
  highlighted,
}: {
  item: MenuItemV2Response;
  onRefine?: () => void;
  onRevert?: () => void;
  onItemClick?: () => void;
  refineDisabled?: boolean;
  priceChange?: PriceChangeRow;
  modifierChanges?: MenuAiModifierChange[];
  change?: MenuAiItemChange;
  highlighted?: boolean;
}) => {
  // Read old/new straight from the change (like the price row + Changes panel)
  // rather than assuming the draft `menu` item holds the new value — the backend
  // may keep the item unchanged and carry the edit only in `changes[]`.
  const nameChanged = !!change?.nameChanged;
  const oldName = nameChanged ? change?.oldName : null;
  const newName = nameChanged ? change?.newName ?? item.name : item.name;

  const descriptionChanged = !!change?.descriptionChanged;
  const oldDescription = descriptionChanged ? change?.oldDescription : null;
  const newDescription = descriptionChanged
    ? change?.newDescription ?? item.description
    : item.description;

  // Prep notes are always shown when the item has them; when the AI touched
  // them the old value is struck through above the new one (like description).
  const prepNotesChanged = !!change?.prepNotesChanged;
  const oldPrepNotes = prepNotesChanged ? change?.oldPrepNotes : null;
  const newPrepNotes = prepNotesChanged
    ? change?.newPrepNotes ?? item.preparationNotes
    : item.preparationNotes;
  return (
    <Div
      id={aiItemDomId(item.id)}
      $box
      $p="12px 24px"
      $pSM="12px 16px"
      $flex
      $alignCenter
      $alignStartSM
      $columnSM
      $between
      $mb="8"
      $gap="8px"
      $gapSM="12px"
      $pointer={!!onItemClick}
      onClick={onItemClick}
      style={{ position: 'relative' }}
    >
      {highlighted && <ItemGlare />}
      <Div $flex $alignStart $gap="16px" $widthSM="100%">
        {/* $flexNone: keep the avatar a fixed circle — otherwise the long
            description widens the text column and flexbox shrinks the image
            horizontally into an ellipse. $alignStart keeps it level with the
            name + price rather than drifting to the middle of the description. */}
        <Div $flexNone>
          {item.imageUrl ? (
            <ItemListImage>
              <Img height="50px" src={item.imageUrl} />
            </ItemListImage>
          ) : (
            <ImagePlaceholderCircle />
          )}
        </Div>
        <Div $flex $column $gap="2px">
          {oldName && (
            <P
              $mb="0"
              $left
              $color={lightTheme.lightGray}
              style={{ textDecoration: 'line-through' }}
            >
              {oldName}
            </P>
          )}
          <P $mb="0" $left>
            {newName}
          </P>
          {priceChange ? (
            <Div $flex $alignCenter $gap="8px" style={{ flexWrap: 'wrap' }}>
              <P
                $m="0"
                $color={lightTheme.lightGray}
                style={{ textDecoration: 'line-through' }}
              >
                {displayCentsInDollars(priceChange.oldCents)}
              </P>
              <H5 $m="0">{displayCentsInDollars(priceChange.newCents)}</H5>
              <Disclaimer $m="0" $color={deltaColor(priceChange.deltaCents)}>
                {signedCents(priceChange.deltaCents)} (
                {signedPct(priceChange.pct)})
              </Disclaimer>
            </Div>
          ) : (
            <H5 $m="0" $color={lightTheme.primary}>
              {formatter.format(centsToDollars(item.price ?? 0))}
            </H5>
          )}
          {oldDescription && (
            <Disclaimer
              $m="0"
              $left
              $color={lightTheme.lightGray}
              style={{ textDecoration: 'line-through' }}
            >
              <b>Description:</b> {oldDescription}
            </Disclaimer>
          )}
          {newDescription && (
            <Disclaimer $m="0" $left $color={lightTheme.darkGray}>
              <b>Description:</b> {newDescription}
            </Disclaimer>
          )}
          {(newPrepNotes || oldPrepNotes) && (
            <Div $mt="4">
              {oldPrepNotes && (
                <Disclaimer
                  $m="0"
                  $left
                  $color={lightTheme.lightGray}
                  style={{ textDecoration: 'line-through' }}
                >
                  <b>Prep notes:</b> {oldPrepNotes}
                </Disclaimer>
              )}
              {newPrepNotes && (
                <Disclaimer $m="0" $left $color={lightTheme.darkGray}>
                  <b>Prep notes:</b> {newPrepNotes}
                </Disclaimer>
              )}
            </Div>
          )}
          {modifierChanges && modifierChanges.length > 0 && (
            <Div $flex $column $gap="4px" $mt="6">
              {modifierChanges.map((m, i) => (
                <Div
                  key={`${m.groupId}-${m.optionName}-${i}`}
                  $flex
                  $alignCenter
                  $gap="6px"
                  style={{ flexWrap: 'wrap' }}
                >
                  <Disclaimer $m="0" $left $color={lightTheme.darkGray}>
                    {m.groupName} • {m.optionName}
                  </Disclaimer>
                  <Disclaimer
                    $m="0"
                    $color={lightTheme.lightGray}
                    style={{ textDecoration: 'line-through' }}
                  >
                    {displayCentsInDollars(m.oldPriceCents)}
                  </Disclaimer>
                  <Disclaimer $m="0" $bold>
                    {displayCentsInDollars(m.newPriceCents)}
                  </Disclaimer>
                  <Disclaimer $m="0" $color={deltaColor(m.deltaCents)}>
                    {signedCents(m.deltaCents)} ({signedPct(m.pct)})
                  </Disclaimer>
                </Div>
              ))}
            </Div>
          )}
        </Div>
      </Div>

      <Div
        $flex
        $alignCenter
        $gap="8px"
        $flexNone
        $widthSM="100%"
        $justifyEndSM
      >
        {onRevert && (
          <RefineIconButton
            $disabled={refineDisabled}
            onClick={
              refineDisabled
                ? undefined
                : (e) => {
                    e.stopPropagation();
                    onRevert();
                  }
            }
            title="Revert this item"
          >
            <UndoIcon color={lightTheme.darkGray} />
          </RefineIconButton>
        )}
        {onRefine && (
          <RefineIconButton
            $disabled={refineDisabled}
            onClick={
              refineDisabled
                ? undefined
                : (e) => {
                    e.stopPropagation();
                    onRefine();
                  }
            }
            title="Refine this item with AI"
          >
            <AIStarsIcon />
          </RefineIconButton>
        )}
      </Div>
    </Div>
  );
};
